import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import PageMeta from "../components/PageMeta";
import LangToggle, { DraftNote } from "../components/LangToggle";
import useLang from "../lib/lang";
import { REVIEWED_DATES, formatReviewedDate } from "../lib/reviewed";

const OVERLINE = "text-[10px] uppercase tracking-[0.22em] text-ember font-accent font-semibold";
const H2 = "font-serif text-lg md:text-xl text-ink leading-tight tracking-tight mb-2 mt-4";
const CARD = "border border-rule bg-white p-4 print:p-3 print:border-black";

const L = {
  en: {
    overline: "Meeting prep · Take this with you",
    title: "Before your meeting — one page.",
    region: "England & Wales",
    intro:
      "For child protection conferences, core groups, child in need meetings and PLO meetings. General information, not legal advice — if you are in proceedings or the PLO, speak to your solicitor first. Legal aid for parents in care proceedings is free and non-means-tested.",
    before: "Before",
    bringTitle: "What to bring",
    during: "During",
    askTitle: "What to ask",
    after: "After — the same day",
    writeTitle: "What to write down",
    wishTitle: "Three things people wish they’d known",
    postureOverline: "The posture that works",
    todayTitle: "Do this today",
    back: "Back to Understanding the system",
    print: "Print or save as PDF",
    cardsLink: "Pocket question cards",
    footerMore: "More guides: truth4parents.com/understanding",
    reviewed: "Last reviewed",
  },
  cy: {
    overline: "Paratoi at gyfarfod · Ewch â hwn gyda chi",
    title: "Cyn eich cyfarfod — un dudalen.",
    region: "Cymru a Lloegr",
    intro:
      "Ar gyfer cynadleddau amddiffyn plant, grwpiau craidd, cyfarfodydd plentyn mewn angen a chyfarfodydd PLO. Gwybodaeth gyffredinol yw hon, nid cyngor cyfreithiol — os ydych mewn achos llys neu'r PLO, siaradwch â'ch cyfreithiwr yn gyntaf. Mae cymorth cyfreithiol i rieni mewn achosion gofal yn rhad ac am ddim, heb brawf modd.",
    before: "Cyn",
    bringTitle: "Beth i ddod gyda chi",
    during: "Yn ystod",
    askTitle: "Beth i'w ofyn",
    after: "Wedyn — yr un diwrnod",
    writeTitle: "Beth i'w ysgrifennu",
    wishTitle: "Tri pheth y byddai pobl wedi hoffi eu gwybod",
    postureOverline: "Yr agwedd sy'n gweithio",
    todayTitle: "Gwnewch hyn heddiw",
    back: "Yn ôl i Ddeall y system",
    print: "Argraffu neu gadw fel PDF",
    cardsLink: "Cardiau cwestiwn poced",
    footerMore: "Rhagor o ganllawiau: truth4parents.com/understanding",
    reviewed: "Adolygwyd ddiwethaf",
  },
};

const BRING = {
  en: [
    <>Your <strong className="text-ink">chronology</strong>, printed and up to date — your dated record of every meeting, letter and call.</>,
    <>The <strong className="text-ink">report or letter</strong> the meeting is about, with tabs or marks on the pages you want to raise.</>,
    <>Your <strong className="text-ink">written list of questions</strong> (middle column) — you will not remember them in the room.</>,
    <>A <strong className="text-ink">supporter</strong> — a friend, relative or advocate. Tell the chair in advance that they are coming.</>,
    <>Evidence of anything they asked for last time — certificates, attendance letters, texts, appointment cards.</>,
    <>Your <strong className="text-ink">solicitor&rsquo;s details</strong>, if you have one.</>,
    <>A pen — and this sheet.</>,
  ],
  cy: [
    <>Eich <strong className="text-ink">cronoleg</strong>, wedi&rsquo;i hargraffu ac yn gyfredol — eich cofnod dyddiedig o bob cyfarfod, llythyr a galwad.</>,
    <>Yr <strong className="text-ink">adroddiad neu&rsquo;r llythyr</strong> y mae&rsquo;r cyfarfod yn ei gylch, gyda nodau ar y tudalennau rydych am eu codi.</>,
    <>Eich <strong className="text-ink">rhestr ysgrifenedig o gwestiynau</strong> (y golofn ganol) — fyddwch chi ddim yn eu cofio yn yr ystafell.</>,
    <><strong className="text-ink">Cefnogwr</strong> — ffrind, perthynas neu eiriolwr. Dywedwch wrth y cadeirydd ymlaen llaw eu bod yn dod.</>,
    <>Tystiolaeth o unrhyw beth y gofynnwyd amdano y tro diwethaf — tystysgrifau, llythyrau presenoldeb, negeseuon testun, cardiau apwyntiad.</>,
    <>Manylion eich <strong className="text-ink">cyfreithiwr</strong>, os oes gennych un.</>,
    <>Beiro — a&rsquo;r ddalen hon.</>,
  ],
};

const ASK = {
  en: [
    <>&ldquo;What exactly is the <strong className="text-ink">concern</strong>, in plain words?&rdquo;</>,
    <>&ldquo;What needs to <strong className="text-ink">change</strong>, by when — and how will you measure it?&rdquo;</>,
    <>&ldquo;Who is doing <strong className="text-ink">what, by what date</strong>?&rdquo; Every action should have a name and a date on it.</>,
    <>&ldquo;What <strong className="text-ink">support</strong> are you offering us — not only what is required of us?&rdquo;</>,
    <>&ldquo;When will I get a copy of the <strong className="text-ink">minutes and the plan</strong>?&rdquo;</>,
    <>&ldquo;If I disagree with something, how is my disagreement <strong className="text-ink">recorded</strong>?&rdquo;</>,
    <>If a word or phrase is unfamiliar — ask them to say it plainly. That is a reasonable request, every time.</>,
  ],
  cy: [
    <>&ldquo;Beth yn union yw&rsquo;r <strong className="text-ink">pryder</strong> — mewn geiriau plaen?&rdquo;</>,
    <>&ldquo;Beth sydd angen <strong className="text-ink">newid</strong>, erbyn pryd — a sut byddwch chi&rsquo;n ei fesur?&rdquo;</>,
    <>&ldquo;Pwy sy&rsquo;n gwneud <strong className="text-ink">beth, erbyn pa ddyddiad</strong>?&rdquo; Dylai enw a dyddiad fod ar bob cam gweithredu.</>,
    <>&ldquo;Pa <strong className="text-ink">gefnogaeth</strong> ydych chi&rsquo;n ei chynnig i ni — nid dim ond beth sy&rsquo;n ofynnol gennym?&rdquo;</>,
    <>&ldquo;Pryd caf i gopi o&rsquo;r <strong className="text-ink">cofnodion a&rsquo;r cynllun</strong>?&rdquo;</>,
    <>&ldquo;Os ydw i&rsquo;n anghytuno â rhywbeth, sut mae fy anghytundeb yn cael ei <strong className="text-ink">gofnodi</strong>?&rdquo;</>,
    <>Os oes gair neu ymadrodd anghyfarwydd — gofynnwch iddyn nhw ei ddweud yn blaen. Mae hynny&rsquo;n gais rhesymol, bob tro.</>,
  ],
};

const WRITE = {
  en: [
    <><strong className="text-ink">Who was there</strong> — names and roles.</>,
    <><strong className="text-ink">What was decided</strong>, and the exact words of anything significant.</>,
    <>What <strong className="text-ink">you</strong> agreed to do, and by when.</>,
    <>What <strong className="text-ink">they</strong> agreed to do, and by when.</>,
    <>Anything you <strong className="text-ink">disagreed</strong> with — and whether your disagreement was noted.</>,
    <>Add one line to your <strong className="text-ink">chronology</strong> while it is fresh.</>,
    <>When the minutes arrive — check them against your notes. Correct any errors <strong className="text-ink">in writing</strong>, and keep a copy of your correction.</>,
  ],
  cy: [
    <><strong className="text-ink">Pwy oedd yno</strong> — enwau a rolau.</>,
    <><strong className="text-ink">Beth benderfynwyd</strong>, ac union eiriau unrhyw beth arwyddocaol.</>,
    <>Beth wnaethoch <strong className="text-ink">chi</strong> gytuno i&rsquo;w wneud, ac erbyn pryd.</>,
    <>Beth wnaethon <strong className="text-ink">nhw</strong> gytuno i&rsquo;w wneud, ac erbyn pryd.</>,
    <>Unrhyw beth roeddech yn <strong className="text-ink">anghytuno</strong> ag ef — ac a gafodd eich anghytundeb ei nodi.</>,
    <>Ychwanegwch un llinell at eich <strong className="text-ink">cronoleg</strong> tra bo&rsquo;r cof yn ffres.</>,
    <>Pan ddaw&rsquo;r cofnodion — gwiriwch nhw yn erbyn eich nodiadau. Cywirwch unrhyw wallau <strong className="text-ink">yn ysgrifenedig</strong>, a chadwch gopi o&rsquo;ch cywiriad.</>,
  ],
};

const WISH = {
  en: [
    <><strong className="text-ink">You can ask for a break</strong> at any point. Stepping out to gather yourself is not held against you — losing your temper can be.</>,
    <><strong className="text-ink">You do not have to sign anything in the room.</strong> It is reasonable to say: &ldquo;I&rsquo;d like to read this properly, or show my solicitor, before I sign.&rdquo;</>,
    <><strong className="text-ink">Calm disagreement, on the record, is not &ldquo;failing to work with professionals.&rdquo;</strong> It is how the record gets corrected.</>,
  ],
  cy: [
    <><strong className="text-ink">Gallwch ofyn am seibiant</strong> unrhyw bryd. Nid yw camu allan i ymdawelu yn cael ei ddal yn eich erbyn — gall colli eich tymer fod.</>,
    <><strong className="text-ink">Does dim rhaid i chi lofnodi unrhyw beth yn yr ystafell.</strong> Mae&rsquo;n rhesymol dweud: &ldquo;Hoffwn ddarllen hwn yn iawn, neu ei ddangos i&rsquo;m cyfreithiwr, cyn llofnodi.&rdquo;</>,
    <><strong className="text-ink">Nid yw anghytuno&rsquo;n bwyllog, ar y cofnod, yn &ldquo;fethiant i weithio gyda gweithwyr proffesiynol.&rdquo;</strong> Dyma sut mae&rsquo;r cofnod yn cael ei gywiro.</>,
  ],
};

const POSTURE = {
  en: (
    <>Steady, specific, written down. <span className="not-italic">Meetings reward the parent who arrives with dates and questions, not the parent with the strongest feelings — however justified those feelings are.</span></>
  ),
  cy: (
    <>Pwyllog, penodol, wedi&rsquo;i ysgrifennu. <span className="not-italic">Mae cyfarfodydd yn gwobrwyo&rsquo;r rhiant sy&rsquo;n cyrraedd gyda dyddiadau a chwestiynau, nid y rhiant â&rsquo;r teimladau cryfaf — pa mor gyfiawn bynnag yw&rsquo;r teimladau hynny.</span></>
  ),
};

const TODAY = {
  en: [
    <>Print this sheet and fill in your questions the night before.</>,
    <>Print and cut out the <span className="print:hidden"><Link to="/understanding/meeting-prep/cards" className="underline" data-testid="meeting-prep-cards-link">pocket question cards</Link></span><span className="hidden print:inline">pocket question cards — truth4parents.com/understanding/meeting-prep/cards</span> to hold in the room.</>,
    <>Afterwards, use the <span className="print:hidden"><Link to="/understanding/meeting-prep/debrief" className="underline" data-testid="meeting-prep-debrief-link">meeting debrief</Link></span><span className="hidden print:inline">meeting debrief — truth4parents.com/understanding/meeting-prep/debrief</span> to get what happened into your chronology the same day.</>,
    <>Start or update your chronology: <span className="print:hidden"><Link to="/understanding/records/chronology" className="underline" data-testid="meeting-prep-chronology-link">the chronology builder</Link></span><span className="hidden print:inline">truth4parents.com/understanding/records/chronology</span>.</>,
    <>Check any unfamiliar term in the glossary: <span className="print:hidden"><Link to="/glossary" className="underline" data-testid="meeting-prep-glossary-link">plain-English glossary</Link></span><span className="hidden print:inline">truth4parents.com/glossary</span>.</>,
    <>Ask someone you trust to come with you.</>,
  ],
  cy: [
    <>Argraffwch y ddalen hon a llenwch eich cwestiynau y noson gynt.</>,
    <>Argraffwch a thorrwch allan y <span className="print:hidden"><Link to="/understanding/meeting-prep/cards" className="underline" data-testid="meeting-prep-cards-link">cardiau cwestiwn poced</Link></span><span className="hidden print:inline">cardiau cwestiwn poced — truth4parents.com/understanding/meeting-prep/cards</span> i&rsquo;w dal yn yr ystafell.</>,
    <>Wedyn, defnyddiwch yr <span className="print:hidden"><Link to="/understanding/meeting-prep/debrief" className="underline" data-testid="meeting-prep-debrief-link">adolygiad ar ôl y cyfarfod</Link></span><span className="hidden print:inline">adolygiad ar ôl y cyfarfod — truth4parents.com/understanding/meeting-prep/debrief</span> i gael yr hyn a ddigwyddodd i mewn i&rsquo;ch cronoleg yr un diwrnod.</>,
    <>Dechreuwch neu diweddarwch eich cronoleg: <span className="print:hidden"><Link to="/understanding/records/chronology" className="underline" data-testid="meeting-prep-chronology-link">yr adeiladwr cronoleg</Link></span><span className="hidden print:inline">truth4parents.com/understanding/records/chronology</span>.</>,
    <>Gwiriwch unrhyw derm anghyfarwydd yn yr <span className="print:hidden"><Link to="/glossary" className="underline" data-testid="meeting-prep-glossary-link">eirfa</Link></span><span className="hidden print:inline">truth4parents.com/glossary</span>.</>,
    <>Gofynnwch i rywun rydych yn ymddiried ynddo ddod gyda chi.</>,
  ],
};

function Item({ children }) {
  return (
    <li className="flex gap-2.5 items-start text-[12.5px] leading-snug">
      <span aria-hidden className="mt-[3px] inline-block w-3.5 h-3.5 border border-ink shrink-0 print:border-black" />
      <span>{children}</span>
    </li>
  );
}

export default function MeetingPrepPage() {
  const [lang, setLang] = useLang();
  const t = L[lang] || L.en;
  const pick = (o) => o[lang] || o.en;

  return (
    <div data-testid="meeting-prep-page" className="cheat-sheet">
      <PageMeta
        title="Before your child protection meeting — one-page prep checklist"
        description="A printable one-page checklist for parents before a child protection conference, core group or child in need meeting: what to bring, what to ask, and what to write down afterwards."
        path="/understanding/meeting-prep"
      />

      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-linen-alt border-b border-rule">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 flex items-center justify-between gap-4 flex-wrap">
          <Link
            to="/understanding"
            data-testid="meeting-prep-back-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-ink"
          >
            <ArrowLeft size={14} strokeWidth={1.5} />
            {t.back}
          </Link>
          <div className="flex items-center gap-3 flex-wrap">
            <LangToggle lang={lang} setLang={setLang} />
            <Link
              to="/understanding/meeting-prep/cards"
              data-testid="meeting-prep-toolbar-cards-link"
              className="inline-flex items-center gap-2 border border-ink/30 text-ink px-5 py-2.5 text-sm font-accent font-semibold tracking-wide hover:border-ink hover:bg-white transition-colors"
            >
              {t.cardsLink}
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="meeting-prep-print-button"
              className="inline-flex items-center gap-2 bg-river text-linen px-5 py-2.5 text-sm font-accent font-semibold tracking-wide hover:bg-river-dark transition-colors"
            >
              <Printer size={14} strokeWidth={1.75} />
              {t.print}
            </button>
          </div>
        </div>
      </div>

      {/* Sheet — sized for A4 */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-8 print:p-0 print:max-w-full">
        <article className="bg-white border border-rule print:border-0 p-8 md:p-10 print:p-6 text-[13px] leading-snug text-ink-muted">
          <header className="flex items-end justify-between gap-6 border-b-2 border-ember pb-3 mb-4">
            <div>
              <div className={OVERLINE}>{t.overline}</div>
              <h1 className="font-serif text-2xl md:text-3xl text-ink tracking-tight mt-1 leading-none">
                {t.title}
              </h1>
            </div>
            <div className="text-right text-[10px] text-ink-muted font-accent">
              <div>truth4parents.com</div>
              <div className="mt-0.5">{t.region}</div>
            </div>
          </header>

          <DraftNote lang={lang} />

          <p className="text-[12px] italic text-ink-muted mb-4 mt-3">{t.intro}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3 print:gap-3">
            <div className={CARD}>
              <div className={OVERLINE}>{t.before}</div>
              <h2 className={H2 + " !mt-1"}>{t.bringTitle}</h2>
              <ul className="space-y-2">
                {pick(BRING).map((item, i) => (
                  <Item key={`b-${i}`}>{item}</Item>
                ))}
              </ul>
            </div>
            <div className={CARD}>
              <div className={OVERLINE}>{t.during}</div>
              <h2 className={H2 + " !mt-1"}>{t.askTitle}</h2>
              <ul className="space-y-2">
                {pick(ASK).map((item, i) => (
                  <Item key={`a-${i}`}>{item}</Item>
                ))}
              </ul>
            </div>
            <div className={CARD}>
              <div className={OVERLINE}>{t.after}</div>
              <h2 className={H2 + " !mt-1"}>{t.writeTitle}</h2>
              <ul className="space-y-2">
                {pick(WRITE).map((item, i) => (
                  <Item key={`w-${i}`}>{item}</Item>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-5 bg-linen-alt border-l-4 border-river p-4 print:bg-white print:border-l-2 print:border-black">
            <h2 className={H2 + " !mt-1"}>{t.wishTitle}</h2>
            <ol className="grid grid-cols-1 md:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2 text-[12.5px]">
              {pick(WISH).map((item, i) => (
                <li key={`wish-${i}`}>{item}</li>
              ))}
            </ol>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 print:grid-cols-2 print:gap-3">
            <div className={CARD}>
              <h2 className={H2 + " !mt-1"}>{t.postureOverline}</h2>
              <p className="font-serif text-ink italic text-base leading-snug">{pick(POSTURE)}</p>
            </div>
            <div className={CARD}>
              <h2 className={H2 + " !mt-1"}>{t.todayTitle}</h2>
              <ul className="space-y-1.5 text-[12.5px]">
                {pick(TODAY).map((item, i) => (
                  <li key={`t-${i}`}>{item}</li>
                ))}
              </ul>
            </div>
          </div>

          <footer className="mt-6 pt-3 border-t border-rule text-[10px] text-ink-muted flex justify-between items-center">
            <div>© You Are Not Alone · truth4parents.com · {t.reviewed} <time dateTime={REVIEWED_DATES["/understanding/meeting-prep"]}>{formatReviewedDate(REVIEWED_DATES["/understanding/meeting-prep"])}</time></div>
            <div>{t.footerMore}</div>
          </footer>
        </article>
      </div>
    </div>
  );
}
